// ============================================
// PARCOURS DU DOSSIER — sous le titre de la fiche
// ============================================
// Ouvert → En cours → Attente client → Résolu → Fermé, chaque étape avec sa
// couleur, son icône et son libellé : l'étape courante est pleine, les
// passées cochées, les suivantes grises. Escaladé et Réouvert s'affichent à
// la place de leur étape. Sous l'étape courante, une phrase dit ce que le
// statut signifie et ce qu'on attend.
//
// Les étapes cliquables sont celles que la barre d'actions propose déjà
// (shared/statuts.ts, actionsStatut) : aucune transition nouvelle. Sur
// téléphone, version compacte : l'étape courante, la progression et un menu
// « Changer le statut ».

import React, { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { TicketStatus } from '../../types';
import { useTelephone } from '../../shared/responsive';
import { IconeStatut, couleurStatut, libelleStatut } from '../../shared/ui/BadgeStatut';
import {
  SENS_STATUT, PARCOURS_PRINCIPAL, PLACE_ETAT_PARTICULIER, actionsStatut, etapesParcours, estEtatParticulier, rangParcours,
  type EtapeParcours,
} from '../../shared/statuts';

/**
 * Résoudre depuis le parcours demande confirmation (un clic sur une étape est
 * vite fait). « Fermé » (archiver) n'est pas confirmé ici : la fiche ouvre sa
 * propre fenêtre d'archivage (case « Sans prévenir le client »).
 */
const A_CONFIRMER: TicketStatus[] = [TicketStatus.RESOLVED];

export const ParcoursStatut: React.FC<{
  status: TicketStatus | string;
  /** Absent : parcours en lecture seule. */
  onChanger?: (vers: TicketStatus) => void;
  /** Enregistrement en cours : les étapes ne sont plus cliquables. */
  occupe?: boolean;
}> = ({ status, onChanger, occupe = false }) => {
  const telephone = useTelephone();
  const [menuOuvert, setMenuOuvert] = useState(false);
  const etapes = etapesParcours(status);
  const actions = onChanger ? actionsStatut(status) : [];
  const possibles = new Set(actions.map(a => a.vers));
  const sens = SENS_STATUT[status as TicketStatus];
  const libelle = libelleStatut(status);
  const couleur = couleurStatut(status);
  const particulier = estEtatParticulier(status);
  const place = PLACE_ETAT_PARTICULIER[status as TicketStatus];

  const changer = (vers: TicketStatus) => {
    if (!onChanger || occupe) return;
    if (A_CONFIRMER.includes(vers) && !window.confirm(`Passer ce dossier en « ${libelleStatut(vers)} » ?`)) return;
    setMenuOuvert(false);
    onChanger(vers);
  };

  const phrase = sens && (
    <p data-testid="sens-statut" className="mt-3 rounded-lg border border-trait border-l-4 bg-papier px-3 py-2 text-sm text-encre" style={{ borderLeftColor: couleur }}>
      <span className="font-bold" style={{ color: couleur }}>{libelle}</span>
      {particulier && place && <span className="text-graphite"> (état particulier, à la place de « {libelleStatut(place)} »)</span>}
      {' — '}{sens.resume} <span className="text-graphite">{sens.attente}</span>
    </p>
  );

  if (telephone) {
    const rang = rangParcours(status);
    return (
      <section aria-label="Parcours du dossier" className="mt-3">
        <div className="flex items-center gap-2">
          <span aria-current="step" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold text-white" style={{ backgroundColor: couleur }}>
            <IconeStatut status={status} className="w-4 h-4" />{libelle}
          </span>
          {rang > 0 && <span className="text-xs text-graphite tnum">Étape {rang} sur {PARCOURS_PRINCIPAL.length}</span>}
          {actions.length > 0 && (
            <button
              type="button"
              onClick={() => setMenuOuvert(o => !o)}
              aria-expanded={menuOuvert}
              aria-controls="menu-statut"
              aria-label="Changer le statut"
              disabled={occupe}
              className="ml-auto inline-flex items-center gap-1 min-h-11 px-3 rounded-lg border border-trait bg-white text-sm font-semibold text-encre disabled:opacity-50"
            >
              Changer <ChevronDown className={`w-4 h-4 transition-transform ${menuOuvert ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
          )}
        </div>
        {/* Progression : un segment par étape, dans la couleur de l'étape une fois atteinte. */}
        <div aria-hidden="true" className="mt-2 flex gap-1">
          {etapes.map(e => (
            <span key={e.etape} className="h-1.5 flex-1 rounded-full" style={{ backgroundColor: e.etat === 'future' ? '#DCE4EF' : couleurStatut(e.affiche) }} />
          ))}
        </div>
        {menuOuvert && actions.length > 0 && (
          <ul id="menu-statut" aria-label="Changer le statut" className="mt-2 surface divide-y divide-trait overflow-hidden">
            {actions.map(a => (
              <li key={a.vers}>
                <button type="button" onClick={() => changer(a.vers)} disabled={occupe}
                  className="w-full flex items-center gap-2 min-h-11 px-3 text-left text-sm hover:bg-brume disabled:opacity-50">
                  <IconeStatut status={a.vers} className="w-4 h-4" style={{ color: couleurStatut(a.vers) }} />
                  <span className="font-semibold text-encre">{a.libelle}</span>
                  <span className="ml-auto text-xs text-graphite">→ {libelleStatut(a.vers)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {phrase}
      </section>
    );
  }

  const cliquables = etapes.filter(e => e.etat !== 'courante' && possibles.has(e.etape));
  return (
    <section aria-label="Parcours du dossier" className="mt-4">
      <ol className="flex items-center gap-1.5">
        {etapes.map((e, i) => (
          <li key={e.etape} className={`flex items-center gap-1.5 min-w-0 ${i < etapes.length - 1 ? 'flex-1' : ''}`}>
            <Etape etape={e} cliquable={!occupe && e.etat !== 'courante' && possibles.has(e.etape)} onClick={() => changer(e.etape)} />
            {i < etapes.length - 1 && (
              <span aria-hidden="true" className="h-0.5 flex-1 min-w-3 rounded-full" style={{ backgroundColor: e.etat === 'passee' ? couleurStatut(e.affiche) : '#DCE4EF' }} />
            )}
          </li>
        ))}
      </ol>
      {phrase}
      {cliquables.length > 0 && (
        <p className="mt-1 text-[11px] text-graphite">Cliquez sur « {cliquables.map(e => libelleStatut(e.etape)).join(' » ou « ')} » pour y passer le dossier.</p>
      )}
    </section>
  );
};

const Etape: React.FC<{ etape: EtapeParcours; cliquable: boolean; onClick: () => void }> = ({ etape, cliquable, onClick }) => {
  const couleur = couleurStatut(etape.affiche);
  const libelle = libelleStatut(etape.affiche);
  const base = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs whitespace-nowrap border transition-colors';
  let classes: string;
  let style: React.CSSProperties;
  let icone: React.ReactNode;
  let etat: string;
  if (etape.etat === 'courante') {
    classes = `${base} font-bold text-white px-3 py-1.5 text-sm`;
    style = { backgroundColor: couleur, borderColor: couleur, boxShadow: `0 0 0 3px ${couleur}26` };
    icone = <IconeStatut status={etape.affiche} className="w-4 h-4" />;
    etat = 'étape actuelle';
  } else if (etape.etat === 'passee') {
    classes = `${base} font-semibold bg-white`;
    style = { color: couleur, borderColor: `${couleur}66` };
    icone = <Check aria-hidden="true" className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={3} />;
    etat = 'étape passée';
  } else {
    classes = `${base} font-medium bg-brume border-trait text-graphite/80`;
    style = {};
    icone = <IconeStatut status={etape.affiche} className="w-3.5 h-3.5 opacity-70" />;
    etat = 'étape à venir';
  }
  const contenu = <>{icone}<span className="truncate">{libelle}</span></>;
  if (cliquable) {
    return (
      <button type="button" onClick={onClick} title={`Passer le dossier en « ${libelle} »`} aria-label={`Passer en « ${libelle} » (${etat})`}
        className={`${classes} cursor-pointer hover:border-encre/40 hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sapin-600 ${etape.etat === 'future' ? 'hover:bg-white hover:text-encre' : ''}`}
        style={style}>
        {contenu}
      </button>
    );
  }
  return (
    <span className={classes} style={style} aria-current={etape.etat === 'courante' ? 'step' : undefined} title={`${libelle} (${etat})`}>
      {contenu}
      <span className="sr-only"> ({etat})</span>
    </span>
  );
};
